/**
 * Network Utilities
 *
 * Zone assignment (proxy), layout math, ego extraction,
 * and data transformation helpers for the ego network visualization.
 */

import { ZONES, ZONE_THRESHOLDS, STRENGTH_THRESHOLDS, CONCENTRIC_LAYOUT } from '../constants/dashboardConfig';

/**
 * Assign a person to a zone based on proxy metrics.
 * MVP uses mention_count, evidence_count, and recency from relationship_rollups.
 * Target: Replace with RPI (Relational Proximity Index) when Tier 2 API is built.
 *
 * @param {Object} person - { mention_count, evidence_count, last_seen }
 * @returns {string} Zone key: 'inner' | 'community' | 'network'
 */
export function assignZone(person) {
  const mentions = person.mention_count || 0;
  const evidence = person.evidence_count || 0;
  const lastSeen = person.last_seen ? new Date(person.last_seen) : null;
  const daysSinceLastSeen = lastSeen
    ? Math.floor((Date.now() - lastSeen.getTime()) / (1000 * 60 * 60 * 24))
    : 999;

  const inner = ZONE_THRESHOLDS.inner;
  if (
    mentions >= inner.minMentions &&
    evidence >= inner.minEvidence &&
    daysSinceLastSeen <= inner.maxDaysSinceLastSeen
  ) {
    return ZONES.INNER;
  }

  const community = ZONE_THRESHOLDS.community;
  if (
    mentions >= community.minMentions &&
    evidence >= community.minEvidence &&
    daysSinceLastSeen <= community.maxDaysSinceLastSeen
  ) {
    return ZONES.COMMUNITY;
  }

  return ZONES.NETWORK;
}

/**
 * Determine connection strength from evidence count between two nodes.
 * @param {number} evidenceCount
 * @returns {'strong' | 'medium' | 'weak'}
 */
export function getConnectionStrength(evidenceCount) {
  if (evidenceCount >= STRENGTH_THRESHOLDS.strong) return 'strong';
  if (evidenceCount >= STRENGTH_THRESHOLDS.medium) return 'medium';
  return 'weak';
}

/**
 * Extract 1.5-hop ego network from full network graph.
 * ego + direct connections (alters) + edges between alters.
 *
 * @param {Object} graphData - { nodes: [], edges: [] }
 * @param {string} egoId - The ego node ID (coach hash or entity identifier)
 * @returns {Object} - { nodes: [], edges: [], egoId }
 */
export function extractEgoNetwork(graphData, egoId) {
  if (!graphData?.nodes?.length) {
    return { nodes: [], edges: [], egoId };
  }

  // If no egoId, treat the node with most connections as ego
  const effectiveEgoId = egoId || findMostConnectedNode(graphData);

  // Find direct alters (1-hop from ego)
  const alterIds = new Set();
  const egoEdges = [];

  (graphData.edges || []).forEach((edge) => {
    if (edge.source === effectiveEgoId) {
      alterIds.add(edge.target);
      egoEdges.push(edge);
    } else if (edge.target === effectiveEgoId) {
      alterIds.add(edge.source);
      egoEdges.push(edge);
    }
  });

  // Find edges between alters (the 0.5-hop)
  const alterEdges = (graphData.edges || []).filter(
    (edge) =>
      alterIds.has(edge.source) &&
      alterIds.has(edge.target) &&
      edge.source !== effectiveEgoId &&
      edge.target !== effectiveEgoId
  );

  // Build node set (ego + alters)
  const nodeIds = new Set([effectiveEgoId, ...alterIds]);
  const nodes = (graphData.nodes || []).filter((node) =>
    nodeIds.has(node.id)
  );

  return {
    nodes,
    edges: [...egoEdges, ...alterEdges],
    egoId: effectiveEgoId,
  };
}

/**
 * Find the node with the most connections.
 */
function findMostConnectedNode(graphData) {
  const degrees = {};
  (graphData.edges || []).forEach((edge) => {
    degrees[edge.source] = (degrees[edge.source] || 0) + 1;
    degrees[edge.target] = (degrees[edge.target] || 0) + 1;
  });

  let maxNode = graphData.nodes?.[0]?.id;
  let maxDegree = 0;
  Object.entries(degrees).forEach(([nodeId, degree]) => {
    if (degree > maxDegree) {
      maxDegree = degree;
      maxNode = nodeId;
    }
  });
  return maxNode;
}

/**
 * Calculate concentric ring positions for nodes.
 * Distributes nodes evenly around each ring.
 *
 * @param {Array} nodes - Nodes with zone assigned
 * @param {string} egoId - Ego node ID
 * @param {number} centerX - Center X coordinate
 * @param {number} centerY - Center Y coordinate
 * @returns {Array} Nodes with x, y positions added
 */
export function calculateConcentricPositions(nodes, egoId, centerX, centerY) {
  const radii = CONCENTRIC_LAYOUT.ringRadii;

  // Separate ego from alters
  const ego = nodes.find((n) => n.id === egoId);
  const alters = nodes.filter((n) => n.id !== egoId);

  // Group by zone
  const groups = {
    [ZONES.INNER]: [],
    [ZONES.COMMUNITY]: [],
    [ZONES.NETWORK]: [],
  };

  alters.forEach((node) => {
    const zone = node.zone || ZONES.NETWORK;
    if (groups[zone]) {
      groups[zone].push(node);
    } else {
      groups[ZONES.NETWORK].push(node);
    }
  });

  const positioned = [];

  // Position ego at center
  if (ego) {
    positioned.push({ ...ego, x: centerX, y: centerY, isEgo: true });
  }

  // Position each zone's nodes evenly around their ring
  Object.entries(groups).forEach(([zone, zoneNodes]) => {
    const radius = radii[zone];
    const count = zoneNodes.length;
    if (count === 0) return;

    const angleStep = (2 * Math.PI) / count;
    // Start from top (-PI/2) and go clockwise
    const startAngle = -Math.PI / 2;

    zoneNodes.forEach((node, i) => {
      const angle = startAngle + i * angleStep;
      positioned.push({
        ...node,
        x: centerX + radius * Math.cos(angle),
        y: centerY + radius * Math.sin(angle),
        isEgo: false,
      });
    });
  });

  return positioned;
}

/**
 * Resolve node labels from hashes using relationship rollup data.
 * Cross-references hashes with known names from entity data.
 *
 * @param {Array} nodes - Nodes from network-graph with hash IDs
 * @param {Object} rollupData - Relationship rollup data from entity
 * @returns {Array} Nodes with resolved labels
 */
export function resolveNodeLabels(nodes, rollupData) {
  if (!rollupData || !nodes) return nodes;

  // Build a hash-to-name mapping from rollup data
  const nameMap = {};

  // Rollup data may have items with personHash and name
  if (Array.isArray(rollupData)) {
    rollupData.forEach((item) => {
      if (item.personHash && item.name) {
        nameMap[item.personHash] = item.name;
      }
      if (item.nodeId && item.label) {
        nameMap[item.nodeId] = item.label;
      }
    });
  } else if (typeof rollupData === 'object') {
    Object.entries(rollupData).forEach(([key, value]) => {
      if (value?.name) nameMap[key] = value.name;
    });
  }

  return nodes.map((node) => ({
    ...node,
    label: nameMap[node.id] || node.label || truncateHash(node.id),
    nameResolved: !!nameMap[node.id],
  }));
}

/**
 * Truncate a hash string for display when name isn't available.
 */
export function truncateHash(hash) {
  if (!hash) return '???';
  if (hash.length <= 10) return hash;
  return `${hash.substring(0, 6)}...${hash.substring(hash.length - 4)}`;
}

/**
 * Get initials from a name string (for avatar display).
 */
export function getInitials(name) {
  if (!name) return '?';
  const parts = name.split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

/**
 * Compute basic network statistics from nodes and edges.
 */
export function computeNetworkStats(nodes, edges) {
  const totalPeople = nodes.filter((n) => !n.isEgo).length;
  const totalConnections = edges.length;

  // Average evidence count across edges as "strength" proxy
  const totalEvidence = edges.reduce((sum, e) => sum + (e.evidence_count || e.weight || 1), 0);
  const avgStrength = totalConnections > 0 ? (totalEvidence / totalConnections).toFixed(1) : '0';

  return {
    totalPeople,
    totalConnections,
    avgStrength,
  };
}

/**
 * Get degree (connection count) for a node.
 */
export function getNodeDegree(nodeId, edges) {
  return edges.filter((e) => e.source === nodeId || e.target === nodeId).length;
}
